import { useCallback, useEffect, useRef, useState } from "react";
import { axiosInstance } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import type { WidgetId } from "../api/dashboard.api";
import type { DashboardPeriod } from "../utils";

/**
 * La configuración del panel, del lado del operador.
 *
 * Hasta acá vivía en `localStorage` con la clave `metrics-prefs-<userId>`: se perdía al cambiar de
 * máquina, al limpiar el navegador y en una ventana privada. Y el layout —los anchos y altos que el
 * operador arrastra— no se guardaba en ningún lado.
 */

export interface WidgetPref {
  id: string;
  visible: boolean;
  order: number;
  page: number;
}

export interface DashboardConfig {
  widgets: WidgetPref[];
  grid: { colFr: number[]; rowH: number[] };
  filters: {
    period: string;
    stores: string[];
    /** La propia tarjeta es un operador más acá (xcons-docs#318) — sin sentinel aparte. */
    operators: number[];
  };
}

const URL = `${API_BASE_URL}/dashboard/config`;

/**
 * Los widgets que el panel ofrece, y cuáles arrancan visibles.
 *
 * Está duplicado con el backend a propósito: es la red para cuando el servidor **no contesta**. Sin
 * esto el panel se queda con `config` en `null`, dibuja los defaults del componente y después
 * descarta cada cambio en silencio — o sea, se ve bien y no responde, que es la peor forma de
 * fallar.
 */
const WIDGETS_CONOCIDOS = [
  "presupuestos",
  "pedidos",
  "comparativa",
  "tasa-conversion",
  "valor-pipeline",
  "presupuestos-riesgo",
  "tiempo-cierre",
  "nuevos-contactos",
  "performance-operadores",
];
const VISIBLES_POR_DEFECTO = ["presupuestos", "pedidos", "comparativa"];

const configPorDefecto = (): DashboardConfig => ({
  widgets: WIDGETS_CONOCIDOS.map((id, i) => ({
    id,
    visible: VISIBLES_POR_DEFECTO.includes(id),
    order: i,
    page: 0,
  })),
  grid: { colFr: [1, 1, 1], rowH: [240, 240] },
  filters: { period: "day", stores: [], operators: [] },
});

/** Cuánto se espera después del último cambio antes de guardar en el servidor. */
const DEBOUNCE_MS = 800;

/**
 * Espejo local de la configuración.
 *
 * El servidor es la fuente de verdad —es lo que hace que el panel viaje entre máquinas— pero no
 * puede ser la ÚNICA copia: mientras el endpoint no esté deployado, o si se cae, el operador
 * acomoda su panel y lo pierde en el próximo F5. El espejo se escribe siempre y se lee sólo cuando
 * el servidor no contesta.
 *
 * Clave distinta de la vieja `metrics-prefs-<userId>` a propósito: esa la borra la migración cuando
 * termina de subirla, y reusarla haría que la migración se pise a sí misma.
 */
const claveEspejo = (userId: string | number) => `dashboard-config-${userId}`;

const leerEspejo = (userId: string | number): DashboardConfig | null => {
  try {
    const raw = localStorage.getItem(claveEspejo(userId));
    if (!raw) return null;
    const c = JSON.parse(raw) as DashboardConfig;
    return Array.isArray(c?.widgets) ? c : null;
  } catch {
    return null;
  }
};

const escribirEspejo = (userId: string | number, cfg: DashboardConfig) => {
  try {
    localStorage.setItem(claveEspejo(userId), JSON.stringify(cfg));
  } catch {
    // Ventana privada, cuota llena. No es motivo para romper nada.
  }
};

export interface UseDashboardConfig {
  config: DashboardConfig | null;
  /** `false` hasta que llegó del servidor. El panel no monta nada antes. */
  cargada: boolean;
  /** Aplica un cambio en memoria y programa el guardado. */
  actualizar: (cambio: Partial<DashboardConfig>) => void;
}

export function useDashboardConfig(userId: string | number | undefined): UseDashboardConfig {
  const [config, setConfig] = useState<DashboardConfig | null>(null);
  const [cargada, setCargada] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendiente = useRef<DashboardConfig | null>(null);

  useEffect(() => {
    if (!userId) return;
    let vivo = true;

    axiosInstance
      .get(URL)
      .then(async ({ data }) => {
        if (!vivo) return;
        let cfg: DashboardConfig = data.config;

        // Migración desde `localStorage`, una sola vez: si el operador ya tenía preferencias en esta
        // máquina y todavía no configuró nada en el servidor, se suben y se borra la clave local.
        // Al revés —dejarla— el panel volvería a la versión vieja en cuanto alguien limpie el server.
        if (!data.personalizado) {
          // Dos orígenes posibles de configuración local, en orden: lo que el operador armó mientras
          // el servidor no estaba, y las preferencias de la versión vieja del panel.
          const espejo = leerEspejo(userId);
          const local = leerPrefsLocales(userId);
          if (espejo) cfg = espejo;
          if (espejo || local) {
            if (local) cfg = fusionar(cfg, local);
            try {
              const { data: guardado } = await axiosInstance.put(URL, cfg);
              cfg = guardado.config ?? cfg;
              localStorage.removeItem(`metrics-prefs-${userId}`);
            } catch {
              // Si falla, se sigue con lo migrado en memoria y la clave local queda: se reintenta
              // en la próxima carga. Perder las preferencias por un 500 sería peor.
            }
          }
        }

        if (vivo) {
          setConfig(cfg);
          escribirEspejo(userId, cfg);
          setCargada(true);
        }
      })
      .catch(() => {
        // Sin servidor el panel tiene que abrir **y funcionar**. Se carga la config por defecto en
        // memoria: el operador puede tildar, ordenar y redimensionar; lo único que no pasa es que
        // se guarde. Dejar `config` en null hacía que la UI se viera normal y no respondiera.
        if (vivo) {
          // El espejo es lo que hace que el panel sobreviva a un F5 aunque el endpoint no exista.
          setConfig(leerEspejo(userId) ?? configPorDefecto());
          setCargada(true);
        }
      });

    return () => {
      vivo = false;
    };
  }, [userId]);

  const actualizar = useCallback(
    (cambio: Partial<DashboardConfig>) => {
      setConfig((prev) => {
        // `prev` nunca es null desde que el catch carga los defaults, pero el guard queda: si
        // alguna vez vuelve a serlo, se arranca de los defaults en vez de perder el cambio.
        const base = prev ?? configPorDefecto();
        const siguiente = { ...base, ...cambio };
        pendiente.current = siguiente;

        // El espejo se escribe YA, sin debounce: es local y barato, y es lo único que hay si el
        // servidor no está. El debounce protege a la red, no al disco.
        if (userId) escribirEspejo(userId, siguiente);

        // Un arrastre de resize emite un evento por píxel. Sin esto serían cientos de PUT por
        // gesto; con esto, uno solo cuando el operador suelta.
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          const aGuardar = pendiente.current;
          if (aGuardar) axiosInstance.put(URL, aGuardar).catch(() => {});
        }, DEBOUNCE_MS);

        return siguiente;
      });
    },
    [userId],
  );

  // Lo que quedó pendiente al desmontar se manda igual: si no, cerrar la pestaña justo después de
  // mover un widget pierde el cambio.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        if (pendiente.current) axiosInstance.put(URL, pendiente.current).catch(() => {});
      }
    },
    [],
  );

  return { config, cargada, actualizar };
}

// ── Migración desde localStorage ──────────────────────────────────────────────

type PrefsLocales = { order?: string[]; activeIds?: string[] };

function leerPrefsLocales(userId: string | number): PrefsLocales | null {
  try {
    const raw = localStorage.getItem(`metrics-prefs-${userId}`);
    if (!raw) return null;
    const p = JSON.parse(raw) as PrefsLocales;
    return Array.isArray(p.order) || Array.isArray(p.activeIds) ? p : null;
  } catch {
    return null;
  }
}

/**
 * Aplica las preferencias viejas sobre los defaults del servidor.
 *
 * Sólo se traen las dos cosas que `localStorage` guardaba —qué widgets estaban activos y en qué
 * orden—. El layout nunca se persistió, así que arranca en el default; el filtro de stores vivía en
 * otra clave y lo migra el panel.
 */
function fusionar(base: DashboardConfig, local: PrefsLocales): DashboardConfig {
  const activos = new Set(local.activeIds ?? []);
  const orden = local.order ?? [];
  return {
    ...base,
    widgets: base.widgets.map((w) => ({
      ...w,
      visible: local.activeIds ? activos.has(w.id) : w.visible,
      order: orden.includes(w.id) ? orden.indexOf(w.id) : w.order,
    })),
  };
}

/** Los ids que el panel conoce, para descartar lo que quedó guardado de versiones viejas. */
export const esWidgetConocido = (id: string, conocidos: readonly WidgetId[] | string[]) =>
  (conocidos as string[]).includes(id);

export type { DashboardPeriod };
